import Dexie, { type Table } from 'dexie';
import type {
  ActivityType,
  Attachment,
  ConcreteCustomer,
  ConcreteDelivery,
  ConcreteDisposalReason,
  ConcreteMix,
  ConcreteTarget,
  CheckItem,
  MachineTransfer,
  VehicleCheck,
  VehicleCheckItem,
  Site,
  EvidenceBlob,
  DailyDiary,
  DiaryActivity,
  DiaryTimeAllocation,
  Machine,
  MachineType,
  Operator,
  OutboxEntry,
  Profile,
  Setting,
  Shift,
  TimeCategory,
  Technician,
  JobCard,
  JobCardLabour,
  JobCardPart,
  FuelTank,
  FuelDipChart,
  FuelIssue,
  FuelReceipt,
  FuelDip,
  PlantProduct,
  PlantRejectReason,
  PlantProduction,
  PlantStockMove,
  PlantTarget,
} from '@/types/models';

/**
 * The local mirror. The UI reads from here and only from here, so losing
 * connectivity changes nothing about how the app behaves - it only delays
 * the sync. Table names match the Postgres table names exactly, which is
 * what lets pull_changes() write straight back into Dexie.
 */
class QuarryDB extends Dexie {
  // master data
  settings!: Table<Setting, string>;
  profiles!: Table<Profile, string>;
  operators!: Table<Operator, string>;
  shifts!: Table<Shift, string>;
  machine_types!: Table<MachineType, string>;
  machines!: Table<Machine, string>;
  time_categories!: Table<TimeCategory, string>;
  activity_types!: Table<ActivityType, string>;
  materials!: Table<Record<string, unknown>, string>;
  products!: Table<Record<string, unknown>, string>;
  locations!: Table<Record<string, unknown>, string>;
  breakdown_categories!: Table<Record<string, unknown>, string>;
  weather_types!: Table<Record<string, unknown>, string>;
  suppliers!: Table<Record<string, unknown>, string>;
  machine_components!: Table<Record<string, unknown>, string>;

  // operations
  daily_diary!: Table<DailyDiary, string>;
  diary_time_allocations!: Table<DiaryTimeAllocation, string>;
  diary_activities!: Table<DiaryActivity, string>;
  weighbridge_tickets!: Table<Record<string, unknown>, string>;
  blasts!: Table<Record<string, unknown>, string>;
  blast_adjustments!: Table<Record<string, unknown>, string>;
  breakdowns!: Table<Record<string, unknown>, string>;
  breakdown_parts!: Table<Record<string, unknown>, string>;
  weather_events!: Table<Record<string, unknown>, string>;
  service_records!: Table<Record<string, unknown>, string>;
  liner_installations!: Table<Record<string, unknown>, string>;
  parts!: Table<Record<string, unknown>, string>;
  part_stock_movements!: Table<Record<string, unknown>, string>;
  purchase_orders!: Table<Record<string, unknown>, string>;
  purchase_order_lines!: Table<Record<string, unknown>, string>;
  production_targets!: Table<Record<string, unknown>, string>;
  attachments!: Table<Attachment, string>;

  // concrete (0035)
  sites!: Table<Site, string>;
  concrete_mixes!: Table<ConcreteMix, string>;
  concrete_customers!: Table<ConcreteCustomer, string>;
  concrete_disposal_reasons!: Table<ConcreteDisposalReason, string>;
  concrete_deliveries!: Table<ConcreteDelivery, string>;
  concrete_targets!: Table<ConcreteTarget, string>;

  // branch fleet (0036)
  machine_transfers!: Table<MachineTransfer, string>;
  check_items!: Table<CheckItem, string>;
  vehicle_checks!: Table<VehicleCheck, string>;
  vehicle_check_items!: Table<VehicleCheckItem, string>;

  // workshop (0037)
  technicians!: Table<Technician, string>;
  job_cards!: Table<JobCard, string>;
  job_card_labour!: Table<JobCardLabour, string>;
  job_card_parts!: Table<JobCardPart, string>;

  // fuel (0042)
  fuel_tanks!: Table<FuelTank, string>;
  fuel_dip_charts!: Table<FuelDipChart, string>;
  fuel_issues!: Table<FuelIssue, string>;
  fuel_receipts!: Table<FuelReceipt, string>;
  fuel_dips!: Table<FuelDip, string>;
  plant_products!: Table<PlantProduct, string>;
  plant_reject_reasons!: Table<PlantRejectReason, string>;
  plant_production!: Table<PlantProduction, string>;
  plant_stock_moves!: Table<PlantStockMove, string>;
  plant_targets!: Table<PlantTarget, string>;

  // client-only
  /** Captured files that have not reached the bucket yet. */
  evidence_blobs!: Table<EvidenceBlob, string>;
  outbox!: Table<OutboxEntry, number>;
  kv!: Table<{ key: string; value: unknown }, string>;

  constructor() {
    super('quarry');
    this.version(1).stores({
      settings: 'key, category',
      profiles: 'id, role',
      operators: 'id, full_name, is_active',
      shifts: 'id, code, sort_order',
      machine_types: 'id, code, sort_order',
      machines: 'id, fleet_no, machine_type_id, status, is_active, updated_at',
      time_categories: 'id, code, sort_order',
      activity_types: 'id, code, sort_order',
      materials: 'id, code',
      products: 'id, code',
      locations: 'id, code, kind',
      breakdown_categories: 'id, code',
      weather_types: 'id, code',
      suppliers: 'id, code',
      machine_components: 'id, machine_id',

      daily_diary: 'id, diary_date, machine_id, shift_id, status, updated_at, [machine_id+diary_date]',
      diary_time_allocations: 'id, diary_id',
      diary_activities: 'id, diary_id, blast_id',
      weighbridge_tickets: 'id, ticket_date, machine_id',
      blasts: 'id, blast_no, status',
      blast_adjustments: 'id, blast_id',
      breakdowns: 'id, machine_id, status, breakdown_date',
      breakdown_parts: 'id, breakdown_id, part_id',
      weather_events: 'id, event_date',
      service_records: 'id, machine_id, status',
      liner_installations: 'id, machine_id, is_current',
      parts: 'id, part_no, is_critical',
      part_stock_movements: 'id, part_id, movement_date',
      purchase_orders: 'id, po_no, status',
      purchase_order_lines: 'id, po_id, part_id',
      production_targets: 'id, period_type, period_start',

      outbox: '++seq, entity, entity_id, next_attempt_at',
      kv: 'key',
    });

    // v2 adds evidence. Dexie merges the two declarations, so an existing
    // tablet upgrades in place rather than losing an unsynced diary.
    this.version(2).stores({
      attachments: 'id, entity_type, entity_id, updated_at, [entity_type+entity_id]',
      evidence_blobs: 'id',
    });

    // v3: branches and the concrete module. Added tables only, so an
    // existing tablet upgrades in place.
    this.version(3).stores({
      sites: 'id, code, kind',
      concrete_mixes: 'id, code, sort_order',
      concrete_customers: 'id, name',
      concrete_disposal_reasons: 'id, code, sort_order',
      concrete_deliveries: 'id, site_id, delivery_date, customer_id, updated_at, [site_id+delivery_date]',
      concrete_targets: 'id, site_id, month',
    });

    // v4: the branch fleet — transfers, the checklist and daily checks.
    this.version(4).stores({
      machine_transfers: 'id, machine_id',
      check_items: 'id, code, machine_type_id, sort_order',
      vehicle_checks: 'id, site_id, machine_id, check_date, [machine_id+check_date], [site_id+check_date]',
      vehicle_check_items: 'id, check_id, item_id',
    });

    // v5: the workshop — technicians, job cards and their labour and parts.
    this.version(5).stores({
      technicians: 'id, name, is_active',
      job_cards: 'id, job_no, site_id, machine_id, status, breakdown_id, service_id, vehicle_check_id',
      job_card_labour: 'id, job_card_id, technician_id, work_date',
      job_card_parts: 'id, job_card_id, part_id',
    });

    // v6: fuel — tanks, dip charts, fills, deliveries and dips.
    this.version(6).stores({
      fuel_tanks: 'id, kind, site_id, machine_id',
      fuel_dip_charts: 'id, tank_id',
      fuel_issues: 'id, site_id, tank_id, machine_id, issued_at, to_tank_id',
      fuel_receipts: 'id, site_id, tank_id, received_at',
      fuel_dips: 'id, site_id, tank_id, dipped_at',
    });

    // v7: the brick plant and pre-cast yard (0063).
    this.version(7).stores({
      plant_products: 'id, operation, code, sort_order',
      plant_reject_reasons: 'id, operation, code, sort_order',
      plant_production: 'id, site_id, operation, production_date, product_id, [site_id+production_date]',
    });

    // v8: stock and dispatch (0064).
    this.version(8).stores({
      plant_stock_moves: 'id, site_id, operation, move_date, product_id, kind, [site_id+move_date]',
    });

    // v9: targets (0065).
    this.version(9).stores({
      plant_targets: 'id, site_id, operation, month, product_id',
    });
  }
}

export const db = new QuarryDB();

/** Tables that pull_changes() returns, in the order it returns them. */
export const SYNC_TABLES = [
  'settings', 'profiles', 'operators', 'shifts', 'machine_types',
  'time_categories', 'activity_types', 'materials', 'products', 'locations',
  'breakdown_categories', 'weather_types', 'suppliers', 'machines',
  'machine_components', 'daily_diary', 'diary_time_allocations',
  'diary_activities', 'weighbridge_tickets', 'blasts', 'blast_adjustments',
  'breakdowns', 'breakdown_parts', 'weather_events', 'service_records',
  'liner_installations', 'parts', 'part_stock_movements', 'purchase_orders',
  'purchase_order_lines', 'production_targets', 'attachments',
  'sites', 'concrete_mixes', 'concrete_customers', 'concrete_disposal_reasons',
  'concrete_deliveries', 'concrete_targets',
  'machine_transfers', 'check_items', 'vehicle_checks', 'vehicle_check_items',
  'technicians', 'job_cards', 'job_card_labour', 'job_card_parts',
  'fuel_tanks', 'fuel_dip_charts', 'fuel_issues', 'fuel_receipts', 'fuel_dips',
  'plant_products', 'plant_reject_reasons', 'plant_production', 'plant_stock_moves', 'plant_targets',
] as const;

export type SyncTable = (typeof SYNC_TABLES)[number];

/* --------------------------- kv helpers --------------------------- */

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const row = await db.kv.get(key);
  return row?.value as T | undefined;
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  await db.kv.put({ key, value });
}

/** Wipe local data on sign-out. Never leave one user's shift on a shared tablet. */
export async function clearLocalData(): Promise<void> {
  await db.delete();
  await db.open();
}
